import * as React from 'react'

import { SheetFooter } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import {
  buildDraftFromSettings,
  methodsEqual,
  personsEqual,
  rowToAuthMethod,
  type DraftState,
  type MethodRow,
} from '@/features/tools/components/authenticate-caller/authenticationDraft'
import type { AuthenticateCallerSettings } from '@/features/tools/types'
import { AuthenticationSettingsEditor } from '@/features/tools/components/authenticate-caller/AuthenticationSettingsEditor'
import { useInitFromServer } from '@/hooks/useInitFromServer'

export function OrganizationAuthenticationForm({
  settings,
  onSubmit,
  onCancel,
}: {
  settings: AuthenticateCallerSettings
  onCancel?: () => void
  onSubmit: (settings: AuthenticateCallerSettings) => Promise<void>
}) {
  const serverDraft = React.useMemo(
    () => buildDraftFromSettings(settings),
    [settings]
  )
  const [draft, setDraft] = React.useState<DraftState>(serverDraft)
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  useInitFromServer(serverDraft, setDraft)

  const isDirty =
    !methodsEqual(draft.methods, serverDraft.methods) ||
    !personsEqual(draft.allowedPersons, serverDraft.allowedPersons)
  const methods = draft.methods
    .filter((row) => row.fields.length > 0)
    .map(rowToAuthMethod)
  const canSubmit =
    isDirty && methods.length > 0 && draft.allowedPersons.length > 0

  const updateRow = (index: number, row: MethodRow) => {
    if (submitting) return
    setDraft((current) => {
      const nextMethods = [...current.methods]
      nextMethods[index] = row
      return { ...current, methods: nextMethods }
    })
  }
  const removeRow = (index: number) => {
    if (submitting) return
    setDraft((current) => ({
      ...current,
      methods: current.methods.filter((_, rowIndex) => rowIndex !== index),
    }))
  }
  const addRow = () => {
    if (submitting) return
    setDraft((current) => ({
      ...current,
      methods: [...current.methods, { fields: [], n: 'all' }],
    }))
  }
  const togglePerson = (person: string) => {
    if (submitting) return
    setDraft((current) => {
      const allowedPersons = new Set(current.allowedPersons)
      if (allowedPersons.has(person)) allowedPersons.delete(person)
      else allowedPersons.add(person)
      return { ...current, allowedPersons: Array.from(allowedPersons) }
    })
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        version: 1,
        methods,
        allowed_persons: draft.allowedPersons,
      })
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'Failed to save settings'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4">
        <AuthenticationSettingsEditor
          draft={draft}
          methodsOverridden={false}
          personsOverridden={false}
          interactionDisabled={submitting}
          showInheritance={false}
          onUpdateRow={updateRow}
          onRemoveRow={removeRow}
          onAddRow={addRow}
          onResetMethods={() => undefined}
          onTogglePerson={togglePerson}
          onResetPersons={() => undefined}
          onAddCustomPerson={(person) =>
            setDraft((current) => ({
              ...current,
              allowedPersons: [...current.allowedPersons, person],
            }))
          }
        />

        {!canSubmit && isDirty ? (
          <p role="alert" className="text-sm text-destructive">
            At least one method and one allowed person are required.
          </p>
        ) : null}
      </div>
      <SheetFooter className="shrink-0">
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={!canSubmit || submitting}>
            Save
          </Button>
        </div>
      </SheetFooter>
    </form>
  )
}
